/**
 * Pure predicate that decides whether a Validation Step pivot column
 * should be rendered under the current Pipeline Filter selection.
 *
 * Sibling of `hideAllPassing.ts` — both are pure column-visibility
 * predicates that `StepBreakdownPivot` ANDs together on every render.
 *
 * Free of React, Redux, and DOM references so it can be reused from
 * memoized selectors and property tests. Callers thread the set of
 * active Step Filter Token column keys in explicitly.
 */

import {
  pivotCellKey,
  type PivotColumn,
  type ValidationPipeline,
} from "./stepBreakdown";

export type PipelineFilterSelection = "ALL" | "LVS" | "HATTRICK";

/**
 * Returns true iff `column` should be rendered given the current
 * `selection` and the set of active Step Filter Token column keys.
 *
 * Semantics:
 * - `"ALL"` always admits the column.
 * - `"LVS"` admits `column.pipeline === "LVS"` OR any column pinned
 *   by an active Step Filter Token (retention rule so a filtered-out
 *   column with an active token stays visible).
 * - `"HATTRICK"` admits symmetrically.
 *
 * Composes with `shouldHideColumn`: callers must AND the results.
 */
export function shouldRenderColumnUnderPipelineFilter(
  column: PivotColumn,
  selection: PipelineFilterSelection,
  activeStepFilterColumnKeys: ReadonlySet<string>
): boolean {
  if (selection === "ALL") {
    return true;
  }
  const pipeline: ValidationPipeline = selection;
  return (
    column.pipeline === pipeline ||
    activeStepFilterColumnKeys.has(pivotCellKey(column))
  );
}
